import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import GeographicChart from '../components/GeographicChart';
import DateClicksChart from '../components/DateClicksChart';
import HourlyChart from '../components/HourlyChart';

/* ─── styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .an-page {
    font-family: 'Inter', system-ui, sans-serif;
    background: #0a0f1e;
    min-height: 100vh;
    padding: 32px 20px 80px;
    color: #e2e8f0;
  }
  .an-inner { max-width: 1200px; margin: 0 auto; }

  /* ── heading bar ── */
  .an-heading {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 28px;
  }
  .an-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(99,102,241,0.18);
    color: #94a3b8;
    padding: 8px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
    text-decoration: none;
  }
  .an-back-btn:hover { background: rgba(99,102,241,0.12); color: #a5b4fc; border-color: rgba(99,102,241,0.3); }

  .an-title {
    font-size: 1.7rem;
    font-weight: 900;
    color: #f1f5f9;
    letter-spacing: -0.03em;
    margin: 0;
  }
  .an-code {
    font-size: 1rem;
    font-weight: 700;
    color: #818cf8;
    background: rgba(99,102,241,0.08);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 8px;
    padding: 3px 10px;
  }

  /* ── generic card ── */
  .an-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.14);
    border-radius: 20px;
    padding: 24px;
    transition: border-color 0.25s;
  }
  .an-card:hover { border-color: rgba(99,102,241,0.26); }

  .an-card-title {
    font-size: 11.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #475569;
    margin: 0 0 14px;
  }

  /* ── top row ── */
  .an-top-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
    align-items: stretch;
  }
  @media (max-width: 700px) {
    .an-top-row { flex-direction: column; }
  }

  /* stat tiles (left column) */
  .an-stat-col { display: flex; flex-direction: column; gap: 14px; min-width: 160px; }
  .an-stat-tile {
    background: rgba(99,102,241,0.08);
    border: 1px solid rgba(99,102,241,0.15);
    border-radius: 16px;
    padding: 20px;
    text-align: center;
  }
  .an-stat-num {
    font-size: 2.2rem;
    font-weight: 900;
    color: #f1f5f9;
    line-height: 1;
    letter-spacing: -0.04em;
    margin-bottom: 4px;
  }
  .an-stat-label { font-size: 12px; color: #64748b; font-weight: 600; }

  /* center (URL info) */
  .an-center { display: flex; flex-direction: column; justify-content: center; gap: 16px; padding: 0 4px; }
  .an-url-box {
    background: rgba(99,102,241,0.06);
    border: 1px solid rgba(99,102,241,0.14);
    border-radius: 14px;
    padding: 18px 20px;
  }
  .an-url-label { font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 8px; }
  .an-url-link {
    color: #818cf8;
    font-size: 14px;
    word-break: break-all;
    text-decoration: none;
    transition: color 0.2s;
  }
  .an-url-link:hover { color: #a5b4fc; }

  /* right column — removed */


  /* alerts */
  .an-alert { border-radius: 10px; padding: 10px 14px; font-size: 13px; margin-bottom: 18px; }
  .an-alert-error   { background: rgba(239,68,68,0.1);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .an-alert-success { background: rgba(34,197,94,0.1);  border: 1px solid rgba(34,197,94,0.2);   color: #86efac; }

  /* ── summary tables grid ── */
  .an-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 18px; margin-bottom: 24px; }

  /* ── click map tabs ── */
  .an-chart-tabs { display: flex; gap: 6px; background: rgba(0,0,0,0.25); padding: 4px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.03); }
  .an-chart-tab {
    padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 700;
    border: none; cursor: pointer; fontFamily: inherit; transition: all 0.2s;
    background: transparent; color: #64748b;
  }
  .an-chart-tab:hover { color: #e2e8f0; }
  .an-chart-tab.active { background: rgba(99,102,241,0.2); color: #a5b4fc; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
  .an-chart-tab:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    filter: grayscale(1);
  }

  .an-mini-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .an-mini-table th {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
    color: #475569; padding: 7px 10px; text-align: left;
    border-bottom: 1px solid rgba(99,102,241,0.12);
  }
  .an-mini-table td {
    padding: 8px 10px;
    color: #94a3b8;
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .an-mini-table tbody tr:hover td { background: rgba(99,102,241,0.05); }
  .an-mini-table tbody tr:last-child td { border-bottom: none; }

  .an-no-data { font-size: 13px; color: #334155; font-style: italic; padding: 10px 0; }

  .an-ref-link { color: #818cf8; text-decoration: none; font-size: 13px; }
  .an-ref-link:hover { text-decoration: underline; }

  /* ── click badge ── */
  .an-click-badge {
    display: inline-flex; align-items: center; justify-content: center;
    padding: 2px 10px; min-width: 36px;
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.18);
    border-radius: 100px; font-size: 12px; font-weight: 700; color: #a5b4fc;
  }

  /* ── individual clicks table ── */
  .an-clicks-title { font-size: 1rem; font-weight: 800; color: #f1f5f9; margin: 0 0 18px; }

  .an-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .an-table th {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
    color: #475569; padding: 9px 12px; text-align: left;
    border-bottom: 1px solid rgba(99,102,241,0.14);
    white-space: nowrap;
  }
  .an-table td { padding: 11px 12px; color: #94a3b8; border-bottom: 1px solid rgba(255,255,255,0.04); vertical-align: middle; }
  .an-table tbody tr:hover td { background: rgba(99,102,241,0.05); }
  .an-table tbody tr:last-child td { border-bottom: none; }

  .an-view-btn {
    padding: 5px 12px;
    background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.22);
    color: #a5b4fc;
    border-radius: 7px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
  }
  .an-view-btn:hover { background: rgba(99,102,241,0.2); }

  /* ── modal ── */
  .an-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.75);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    z-index: 1000; padding: 20px;
  }
  .an-modal {
    background: #131929;
    border: 1px solid rgba(99,102,241,0.22);
    border-radius: 20px;
    padding: 32px;
    max-width: 820px;
    width: 100%;
    max-height: 88vh;
    overflow-y: auto;
    box-shadow: 0 28px 60px rgba(0,0,0,0.55);
    animation: anFadeUp 0.25s ease-out both;
  }
  @keyframes anFadeUp {
    from { opacity: 0; transform: translateY(16px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .an-modal-title { font-size: 1.25rem; font-weight: 900; color: #f1f5f9; margin: 0 0 4px; }
  .an-modal-sub   { font-size: 13px; color: #475569; margin: 0 0 24px; }

  .an-modal-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; margin-bottom: 18px; }
  .an-detail-card {
    background: rgba(99,102,241,0.06);
    border: 1px solid rgba(99,102,241,0.12);
    border-radius: 14px;
    padding: 16px;
  }
  .an-detail-card h4 { font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.07em; margin: 0 0 12px; }
  .an-detail-row { font-size: 13px; color: #94a3b8; margin: 4px 0; }
  .an-detail-row strong { color: #e2e8f0; }

  .an-ua-box {
    background: rgba(0,0,0,0.3);
    border: 1px solid rgba(99,102,241,0.1);
    border-radius: 10px;
    padding: 12px 14px;
    font-family: monospace;
    font-size: 11.5px;
    color: #64748b;
    word-break: break-all;
    margin-top: 10px;
  }

  .an-modal-close {
    display: block;
    width: 100%;
    padding: 12px;
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.2);
    color: #a5b4fc;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
    margin-top: 18px;
  }
  .an-modal-close:hover { background: rgba(99,102,241,0.2); }

  /* error / loading states */
  .an-state-page {
    background: #0a0f1e; min-height: 100vh;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 16px; font-family: 'Inter', sans-serif;
    color: #64748b; font-size: 15px;
  }
  .an-state-btn {
    padding: 10px 20px; background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.22); color: #a5b4fc;
    border-radius: 10px; font-size: 14px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: all 0.2s;
  }
  .an-state-btn:hover { background: rgba(99,102,241,0.22); }

  /* ── score card ── */
  .an-score-section { margin-bottom: 24px; }
  .an-score-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.14);
    border-radius: 20px;
    padding: 24px;
  }
  .an-score-top {
    display: flex; align-items: center; gap: 24px;
    margin-bottom: 24px; flex-wrap: wrap;
  }
  .an-score-ring {
    width: 80px; height: 80px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.8rem; font-weight: 900; color: #f1f5f9;
    flex-shrink: 0;
  }
  .an-score-ring-green  { background: rgba(34,197,94,0.15); border: 3px solid rgba(34,197,94,0.5); }
  .an-score-ring-yellow { background: rgba(234,179,8,0.15);  border: 3px solid rgba(234,179,8,0.5); }
  .an-score-ring-red    { background: rgba(239,68,68,0.15);  border: 3px solid rgba(239,68,68,0.5); }
  .an-score-label { font-size: 14px; font-weight: 700; margin-bottom: 2px; }
  .an-score-sub { font-size: 12px; color: #64748b; }
  .an-score-refresh {
    margin-left: auto; padding: 8px 16px;
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2);
    color: #a5b4fc; border-radius: 8px; font-size: 12px; font-weight: 700;
    cursor: pointer; font-family: inherit; transition: all 0.2s;
  }
  .an-score-refresh:hover { background: rgba(99,102,241,0.2); }
  .an-score-refresh:disabled { opacity: 0.4; cursor: not-allowed; }
  .an-score-bars { display: flex; flex-direction: column; gap: 10px; }
  .an-score-bar-row { display: flex; align-items: center; gap: 12px; }
  .an-score-bar-label { font-size: 12px; font-weight: 600; color: #94a3b8; width: 150px; flex-shrink: 0; }
  .an-score-bar-track {
    flex: 1; height: 8px; background: rgba(255,255,255,0.06);
    border-radius: 4px; overflow: hidden;
  }
  .an-score-bar-fill { height: 100%; border-radius: 4px; transition: width 0.5s ease; }
  .an-score-bar-fill-green  { background: linear-gradient(90deg, #22c55e, #4ade80); }
  .an-score-bar-fill-yellow { background: linear-gradient(90deg, #eab308, #facc15); }
  .an-score-bar-fill-red    { background: linear-gradient(90deg, #ef4444, #f87171); }
  .an-score-bar-val { font-size: 11px; font-weight: 700; color: #64748b; width: 40px; text-align: right; flex-shrink: 0; }
  .an-score-bar-detail { font-size: 11px; color: #475569; flex-shrink: 0; }

  /* score info icon */
  .an-score-info-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 12px; border-radius: 100px;
    background: rgba(99,102,241,0.12); border: 1px solid rgba(99,102,241,0.25);
    color: #a5b4fc; font-size: 12px; font-weight: 700;
    cursor: pointer; margin-left: 8px; transition: all 0.2s;
    font-family: inherit; line-height: 1; vertical-align: middle;
  }
  .an-score-info-btn:hover { background: rgba(99,102,241,0.25); transform: scale(1.1); }

  /* score info modal */
  .an-info-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.75); backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    z-index: 1001; padding: 20px;
  }
  .an-info-modal {
    background: #131929; border: 1px solid rgba(99,102,241,0.22);
    border-radius: 20px; padding: 32px; max-width: 560px; width: 100%;
    max-height: 85vh; overflow-y: auto;
    box-shadow: 0 28px 60px rgba(0,0,0,0.55);
    animation: anFadeUp 0.25s ease-out both;
  }
  .an-info-title { font-size: 1.2rem; font-weight: 900; color: #f1f5f9; margin: 0 0 6px; }
  .an-info-subtitle { font-size: 13px; color: #64748b; margin: 0 0 20px; }
  .an-info-section { margin-bottom: 18px; }
  .an-info-section h4 { font-size: 12px; font-weight: 700; color: #818cf8; text-transform: uppercase; letter-spacing: 0.06em; margin: 0 0 8px; }
  .an-info-section p { font-size: 13px; color: #94a3b8; line-height: 1.6; margin: 0 0 8px; }
  .an-info-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 12px; }
  .an-info-table th { text-align: left; color: #475569; font-weight: 700; padding: 6px 8px; border-bottom: 1px solid rgba(99,102,241,0.12); }
  .an-info-table td { color: #94a3b8; padding: 6px 8px; border-bottom: 1px solid rgba(255,255,255,0.04); }
  .an-info-legend { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 6px; }
  .an-info-legend-item { font-size: 12px; display: flex; align-items: center; gap: 5px; color: #94a3b8; }

  /* ── row split ── */
  .an-row-split {
    display: flex;
    gap: 24px;
    margin-bottom: 24px;
    align-items: stretch;
  }
  .an-row-split > * {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-bottom: 0 !important;
  }
  .an-row-split .an-score-card,
  .an-row-split .an-card {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  @media (max-width: 1024px) {
    .an-row-split {
      flex-direction: column;
    }
  }

  /* ── self-healing panel ── */
  .an-heal-card {
    background: rgba(16,185,129,0.04);
    border: 1px solid rgba(16,185,129,0.18);
    border-radius: 20px;
    padding: 28px;
    margin-bottom: 24px;
  }
  .an-heal-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
  .an-heal-title { font-size: 1.05rem; font-weight: 900; color: #f1f5f9; margin: 0; }
  .an-heal-status-row { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 18px; }
  .an-heal-stat {
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px; padding: 16px 20px; flex: 1; min-width: 120px;
  }
  .an-heal-stat-label { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 6px; }
  .an-heal-stat-val { font-size: 1.2rem; font-weight: 900; color: #f1f5f9; }
  .an-heal-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 12px; border-radius: 100px;
    font-size: 12px; font-weight: 800; letter-spacing: 0.03em;
  }
  .an-heal-badge-healthy { background: rgba(34,197,94,0.12); border: 1px solid rgba(34,197,94,0.25); color: #86efac; }
  .an-heal-badge-healing { background: rgba(234,179,8,0.12);  border: 1px solid rgba(234,179,8,0.25);  color: #fde047; }
  .an-heal-badge-failed  { background: rgba(239,68,68,0.12);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .an-heal-badge-none    { background: rgba(100,116,139,0.1); border: 1px solid rgba(100,116,139,0.15); color: #64748b; }
  .an-heal-toggle {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 18px; border-radius: 10px;
    font-size: 13px; font-weight: 700;
    border: none; cursor: pointer; font-family: inherit;
    transition: all 0.2s;
  }
  .an-heal-toggle-on  { background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.25); color: #34d399; }
  .an-heal-toggle-off { background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2);  color: #a5b4fc; }
  .an-heal-toggle:disabled { opacity: 0.4; cursor: not-allowed; }
  .an-heal-event-log { margin-top: 20px; }
  .an-heal-event-log h4 { font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.07em; margin: 0 0 12px; }
  .an-heal-event {
    display: flex; gap: 12px; align-items: flex-start;
    padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.04);
    font-size: 12.5px;
  }
  .an-heal-event:last-child { border-bottom: none; }
  .an-heal-event-time { color: #475569; white-space: nowrap; min-width: 120px; }
  .an-heal-event-detail { color: #94a3b8; flex: 1; }
  .an-heal-config { margin-top: 20px; padding-top: 20px; border-top: 1px solid rgba(255,255,255,0.06); }
  .an-heal-config h4 { font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.07em; margin: 0 0 14px; }
  .an-heal-backup-row { display: flex; gap: 8px; align-items: center; margin-bottom: 8px; }
  .an-heal-backup-input {
    flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(99,102,241,0.18);
    color: #e2e8f0; border-radius: 10px; padding: 9px 14px; font-size: 13px; font-family: inherit;
  }
  .an-heal-backup-input:focus { outline: none; border-color: rgba(99,102,241,0.4); }
  .an-heal-remove-btn {
    background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.2); color: #fca5a5;
    border-radius: 8px; padding: 7px 12px; cursor: pointer; font-size: 12px; font-weight: 700;
    font-family: inherit; transition: all 0.2s;
  }
  .an-heal-remove-btn:hover { background: rgba(239,68,68,0.18); }
  .an-heal-add-btn {
    background: rgba(99,102,241,0.1); border: 1px solid rgba(99,102,241,0.2); color: #a5b4fc;
    border-radius: 8px; padding: 7px 14px; cursor: pointer; font-size: 12px; font-weight: 700;
    font-family: inherit; transition: all 0.2s; margin-top: 4px;
  }
  .an-heal-add-btn:hover { background: rgba(99,102,241,0.18); }
  .an-heal-check-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(234,179,8,0.1); border: 1px solid rgba(234,179,8,0.2); color: #fde047;
    border-radius: 10px; padding: 7px 16px; cursor: pointer; font-size: 13px; font-weight: 700;
    font-family: inherit; transition: all 0.2s;
  }
  .an-heal-check-btn:hover { background: rgba(234,179,8,0.18); }
  .an-heal-check-btn:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* ─── component ──────────────────────────────────────────────────────────── */
const Analytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.plan === 'pro' || user?.plan === 'business';

  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedClick, setSelectedClick] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [scoreData, setScoreData] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreInfoOpen, setScoreInfoOpen] = useState(false);
  const [healInfoOpen, setHealInfoOpen] = useState(false);

  // Click Map state
  const [clickMapTab, setClickMapTab] = useState('geo');   // 'geo' | 'date' | 'hourly'
  const [geoRange, setGeoRange] = useState(30);
  const [hourlyRange, setHourlyRange] = useState(30);

  // Self-Healing state
  const [healingData, setHealingData] = useState(null);
  const [healingEnrolled, setHealingEnrolled] = useState(false);
  const [healingLoading, setHealingLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [healSaveMsg, setHealSaveMsg] = useState('');
  const [backupUrls, setBackupUrls] = useState(['']);
  const [alertEmail, setAlertEmail] = useState('');
  const [showHealConfig, setShowHealConfig] = useState(false);
  const [healEventPage, setHealEventPage] = useState(1);
  const [clicksPage, setClicksPage] = useState(1);
  const CLICKS_PER_PAGE = 10;
  const HEAL_EVENTS_PER_PAGE = 8;

  useEffect(() => { fetchAnalytics(geoRange, hourlyRange); fetchHealingData(); }, [id]);
  useEffect(() => { if (analytics) fetchAnalytics(geoRange, hourlyRange); }, [geoRange, hourlyRange]);

  const fetchAnalytics = async (days = 30, hdays = 30) => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/analytics/link/${id}?days=${days}&hdays=${hdays}`);
      setAnalytics(r.data);
      // Use cached score from analytics response
      if (r.data.linkScore) setScoreData(r.data.linkScore);
    } catch {
      setError('Failed to fetch analytics or feature not available');
    }
  };

  const fetchScore = async (forceRefresh = false) => {
    setScoreLoading(true);
    try {
      const url = `${API_BASE_URL}/api/links/${id}/score${forceRefresh ? '?refresh=1' : ''}`;
      const r = await axios.get(url);
      setScoreData(r.data);
    } catch { /* skip — not business plan */ }
    finally { setScoreLoading(false); }
  };

  // Fetch score on mount if not available from analytics
  useEffect(() => { if (analytics && !scoreData) fetchScore(); }, [analytics]);

  // ── Self-Healing functions ────────────────────────────────────────────────
  const fetchHealingData = async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/healing/link/${id}`);
      setHealingData(r.data);
      setHealingEnrolled(r.data.enrolled || false);
      if (r.data.config?.alert_email) setAlertEmail(r.data.config.alert_email);
      if (r.data.backups?.length) setBackupUrls(r.data.backups.map(b => b.url));
      else setBackupUrls(['']);
    } catch { /* not enrolled or not available */ }
  };

  const toggleHealEnrol = async () => {
    setHealingLoading(true);
    try {
      if (healingEnrolled) {
        await axios.delete(`${API_BASE_URL}/api/healing/link/${id}/enrol`);
        setHealingEnrolled(false);
        setHealingData(null);
      } else {
        await axios.post(`${API_BASE_URL}/api/healing/link/${id}/enrol`);
        setHealingEnrolled(true);
        fetchHealingData();
      }
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Self-Healing: Failed to update');
    } finally {
      setHealingLoading(false);
    }
  };

  const forceCheck = async () => {
    setCheckLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/healing/link/${id}/check`);
      await fetchHealingData();
      setHealSaveMsg('✅ Health check complete');
      setTimeout(() => setHealSaveMsg(''), 4000);
    } catch (err) {
      setHealSaveMsg('❌ Check failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setCheckLoading(false);
    }
  };

  const saveHealConfig = async () => {
    try {
      const cleanBackups = backupUrls.filter(u => u.trim());
      await axios.post(`${API_BASE_URL}/api/healing/link/${id}/config`, {
        alertEmail: alertEmail || null,
        backups: cleanBackups,
      });
      await fetchHealingData();
      setHealSaveMsg('✅ Healing config saved');
      setTimeout(() => setHealSaveMsg(''), 4000);
    } catch (err) {
      setHealSaveMsg('❌ ' + (err.response?.data?.error || 'Failed to save'));
    }
  };

  const openModal = (click) => { setSelectedClick(click); setModalIsOpen(true); };
  const closeModal = () => { setModalIsOpen(false); setSelectedClick(null); };

  /* ── loading / error states ── */
  if (error) {
    return (
      <div className="an-state-page">
        <style>{STYLES}</style>
        <p style={{ color: '#fca5a5' }}>{error}</p>
        <button className="an-state-btn" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="an-state-page">
        <style>{STYLES}</style>
        <p>Loading analytics…</p>
      </div>
    );
  }

  /* ── helpers ── */

  const { short_code, url_type, brand_name } = analytics.link;
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const siteOrigin = window.location.origin; // e.g. http://localhost:5000 or https://rdrto.com
  let shortUrl;
  if (url_type === 'brand_path' && brand_name) {
    shortUrl = `${API_BASE_URL}/${brand_name}/${short_code}`;
  } else if (url_type === 'brand_subdomain' && brand_name) {
    // In local dev, subdomain DNS (brand.localhost) doesn't resolve — use path format instead
    if (isLocal) {
      shortUrl = `${API_BASE_URL}/${brand_name}/${short_code}`;
    } else {
      const { protocol, host } = window.location;
      shortUrl = `${protocol}//${brand_name}.${host}/${short_code}`;
    }
  } else {
    shortUrl = `${siteOrigin}/${short_code}`;
  }


  /* mini-table helper */
  const MiniTable = ({ title, rows, cols, wrapperStyle }) => (
    <div className="an-card" style={wrapperStyle}>
      <p className="an-card-title">{title}</p>
      {rows.length === 0 ? (
        <p className="an-no-data">No data yet</p>
      ) : (
        <table className="an-mini-table">
          <thead>
            <tr>{cols.map(c => <th key={c.key}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.slice(0, 6).map((row, i) => (
              <tr key={i}>
                {cols.map(c => (
                  <td key={c.key}>
                    {c.render ? c.render(row[c.key], row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="an-page">
      <style>{STYLES}</style>
      <div className="an-inner">

        {/* ── Heading ── */}
        <div className="an-heading">
          <button className="an-back-btn" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <h1 className="an-title">Analytics</h1>
          <span className="an-code">{analytics.link.short_code}</span>
        </div>

        {/* ── Top row: all four items in one line ── */}
        <div className="an-top-row">

          {/* Total Clicks */}
          <div className="an-stat-tile" style={{ minWidth: 120 }}>
            <div className="an-stat-num">{analytics.totalClicks}</div>
            <div className="an-stat-label">Total Clicks</div>
          </div>

          {/* Active Days */}
          <div className="an-stat-tile" style={{ minWidth: 120 }}>
            <div className="an-stat-num">{analytics.dailyClicks.length}</div>
            <div className="an-stat-label">Active Days</div>
          </div>

          {/* Short URL */}
          <div className="an-url-box" style={{ flex: 1, minWidth: 200 }}>
            <p className="an-url-label">Short URL</p>
            <a className="an-url-link" href={shortUrl} target="_blank" rel="noopener noreferrer">{shortUrl}</a>
          </div>

          {/* Destination */}
          <div className="an-url-box" style={{ flex: 2, minWidth: 200 }}>
            <p className="an-url-label">Destination</p>
            <a className="an-url-link" href={analytics.link.long_url} target="_blank" rel="noopener noreferrer">{analytics.link.long_url}</a>
          </div>

        </div>

        {/* ── Self-Healing Panel (Pro/Business only) ── */}
        {isPro && (
          <div className="an-heal-card">
            {/* Header row */}
            <div className="an-heal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h2 className="an-heal-title">🏥 Self-Healing Links</h2>
                <button className="an-score-info-btn" onClick={() => setHealInfoOpen(true)} title="What is this?">ℹ️ What is it?</button>
                {healingData?.status && (
                  <span className={`an-heal-badge an-heal-badge-${healingData.status.status || 'none'}`}>
                    {healingData.status.status === 'healthy' ? '🟢 Healthy' :
                      healingData.status.status === 'healing' ? '🔄 Healing' :
                        healingData.status.status === 'failed' ? '❌ Failed' : '⚪ Not checked'}
                  </span>
                )}
                {healSaveMsg && (
                  <span style={{ fontSize: 13, color: healSaveMsg.startsWith('✅') ? '#86efac' : '#fca5a5', fontWeight: 500 }}>
                    {healSaveMsg}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {healingEnrolled && (
                  <button className="an-heal-check-btn" onClick={forceCheck} disabled={checkLoading}>
                    {checkLoading ? '⏳ Checking…' : '🔁 Check Now'}
                  </button>
                )}
                <button
                  className={`an-heal-toggle ${healingEnrolled ? 'an-heal-toggle-on' : 'an-heal-toggle-off'}`}
                  onClick={toggleHealEnrol}
                  disabled={healingLoading}
                >
                  {healingLoading ? '…' : healingEnrolled ? '🏥 Healing ON' : '+ Enable Healing'}
                </button>
              </div>
            </div>

            {!healingEnrolled && (
              <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65 }}>
                Enable Self-Healing to automatically detect broken destination URLs and switch to backup links. Visitors are always redirected to a healthy URL.
              </p>
            )}

            {/* 3-column layout when enrolled */}
            {healingEnrolled && (
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 8 }}>

                {/* LEFT — Status */}
                <div style={{ flex: '1 1 200px', minWidth: 180 }}>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 12px' }}>📊 Status</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="an-heal-stat">
                      <div className="an-heal-stat-label">Primary URL</div>
                      <div className="an-heal-stat-val" style={{ fontSize: '0.85rem', color: healingData?.status?.active_url ? '#fca5a5' : healingData?.status?.status === 'healthy' ? '#86efac' : '#fca5a5' }}>
                        {healingData?.status
                          ? (healingData.status.active_url ? '❌ Unhealthy' : healingData.status.status === 'healthy' ? '✅ Healthy' : '❌ Down')
                          : '—'}
                        {healingData?.status?.http_code ? ` (HTTP ${healingData.status.http_code})` : ''}
                      </div>
                    </div>
                    {healingData?.status?.active_url && (
                      <div className="an-heal-stat">
                        <div className="an-heal-stat-label">Backup URL (Active)</div>
                        <div className="an-heal-stat-val" style={{ fontSize: '0.8rem', color: '#86efac', wordBreak: 'break-all' }}>
                          ✅ {healingData.status.active_url}
                        </div>
                      </div>
                    )}
                    <div className="an-heal-stat">
                      <div className="an-heal-stat-label">Last Check</div>
                      <div className="an-heal-stat-val" style={{ fontSize: '0.82rem' }}>
                        {healingData?.status?.last_checked_at
                          ? new Date(healingData.status.last_checked_at).toLocaleString()
                          : 'Pending…'}
                      </div>
                    </div>
                    {healingData?.status?.response_ms != null && (
                      <div className="an-heal-stat">
                        <div className="an-heal-stat-label">Response Time</div>
                        <div className="an-heal-stat-val">{healingData.status.response_ms} ms</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* CENTER — Config */}
                <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 12px' }}>⚙️ Configuration</h4>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>🔗 Backup URL</div>
                    <input
                      className="an-heal-backup-input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                      type="url"
                      placeholder="https://your-backup-url.com"
                      value={backupUrls[0] || ''}
                      onChange={e => setBackupUrls([e.target.value])}
                    />
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>📧 Alert Email</div>
                    <input
                      className="an-heal-backup-input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                      type="email"
                      placeholder="owner@example.com"
                      value={alertEmail}
                      onChange={e => setAlertEmail(e.target.value)}
                    />
                  </div>

                  <button
                    onClick={saveHealConfig}
                    style={{
                      marginTop: 8, width: '100%', boxSizing: 'border-box',
                      padding: '10px 0', borderRadius: 10, border: 'none',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff', fontSize: 13, fontWeight: 700,
                      fontFamily: 'inherit', cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(16,185,129,0.45)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 14px rgba(16,185,129,0.3)'; }}
                  >
                    💾 Save Config
                  </button>
                </div>

                {/* RIGHT — Event Log */}
                <div style={{ flex: '2 1 260px', minWidth: 240 }}>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 12px' }}>📋 Healing Event Log</h4>
                  {healingData?.events?.length > 0 ? (() => {
                    const allEvents = healingData.events;
                    const totalPages = Math.max(1, Math.ceil(allEvents.length / HEAL_EVENTS_PER_PAGE));
                    const pageEvents = allEvents.slice((healEventPage - 1) * HEAL_EVENTS_PER_PAGE, healEventPage * HEAL_EVENTS_PER_PAGE);
                    return (
                      <>
                        <div className="an-heal-event-log" style={{ marginTop: 0 }}>
                          {pageEvents.map(evt => (
                            <div key={evt.id} className="an-heal-event">
                              <span className="an-heal-event-time" style={{ minWidth: 'auto', fontSize: 11 }}>{new Date(evt.created_at).toLocaleString()}</span>
                              <span className="an-heal-event-detail">
                                {evt.event_type === 'check_ok' && '✅ '}
                                {evt.event_type === 'check_fail' && '❌ '}
                                {evt.event_type === 'healing_start' && '🔄 '}
                                {evt.event_type === 'backup_activated' && '🔀 '}
                                {evt.event_type === 'primary_restored' && '🟢 '}
                                {evt.event_type === 'all_failed' && '🚨 '}
                                {evt.event_type === 'notify_sent' && '📧 '}
                                {evt.detail}
                              </span>
                            </div>
                          ))}
                        </div>
                        {totalPages > 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 8 }}>
                            <span style={{ fontSize: 12, color: '#475569' }}>Page {healEventPage}/{totalPages}</span>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                className="an-heal-check-btn"
                                style={{ padding: '4px 12px', fontSize: 12 }}
                                onClick={() => setHealEventPage(p => Math.max(1, p - 1))}
                                disabled={healEventPage === 1}
                              >← Prev</button>
                              <button
                                className="an-heal-check-btn"
                                style={{ padding: '4px 12px', fontSize: 12 }}
                                onClick={() => setHealEventPage(p => Math.min(totalPages, p + 1))}
                                disabled={healEventPage === totalPages}
                              >Next →</button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })() : (
                    <p style={{ fontSize: 13, color: '#334155', fontStyle: 'italic' }}>No events yet. Events appear after the first health check.</p>
                  )}
                </div>

              </div>
            )}
          </div>
        )}

        <div className="an-row-split">
          {/* ── Link Intelligence Score (Business only) ── */}
          {scoreData && (() => {
            const { score, breakdown, cachedAt, penalty } = scoreData;
            const color = score >= 80 ? 'green' : score >= 50 ? 'yellow' : 'red';
            const label = score >= 80 ? 'Healthy' : score >= 50 ? 'Fair' : 'Poor';
            const light = color === 'green' ? '🟢' : color === 'yellow' ? '🟡' : '🔴';
            const signalNames = {
              // Top-level categories
              engagement: '📈 Engagement',
              trafficTrust: '🛡️ Traffic Trust',
              destinationHealth: '🌐 Destination Health',
              compliance: '✅ Compliance',
              // Sub-signals (shown when breakdown is nested)
              trend: 'Click Trend', diversity: 'IP Diversity', volume: 'Volume',
              botRisk: 'Bot Risk', ipDiversity: 'IP Spread', velocity: 'Velocity',
              status: 'HTTP Status', speed: 'Speed', ssl: 'SSL/HTTPS',
              abuse: 'Abuse Reports', active: 'Active Status', expiry: 'Expiry',
            };

            return (
              <div className="an-score-section" style={{ marginBottom: 0 }}>
                <div className="an-score-card" style={{ height: '100%' }}>
                  {/* Section Header */}
                  <div className="an-heal-header" style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <h2 className="an-heal-title">Link Intelligence Score</h2>
                      <button className="an-score-info-btn" style={{ margin: 0 }} onClick={() => setScoreInfoOpen(true)} title="What is this?">ℹ️ What is it?</button>
                    </div>
                    <button className="an-score-refresh" onClick={() => fetchScore(true)} disabled={scoreLoading}>
                      {scoreLoading ? '⏳ Checking…' : '🔄 Refresh'}
                    </button>
                  </div>

                  <div className="an-score-top">
                    <div className={`an-score-ring an-score-ring-${color}`}>{score}</div>
                    <div>
                      <div className="an-score-label" style={{ color: color === 'green' ? '#86efac' : color === 'yellow' ? '#fde047' : '#fca5a5' }}>
                        {light} {label}
                      </div>
                      <div className="an-score-sub" style={{ marginBottom: 4 }}>
                        Link Intelligence Score
                      </div>
                      {cachedAt && <div className="an-score-sub">Last checked: {new Date(cachedAt).toLocaleString()}</div>}
                    </div>
                  </div>
                  {penalty && (
                    <div style={{
                      margin: '10px 0 4px',
                      padding: '8px 12px',
                      background: 'rgba(239,68,68,0.08)',
                      border: '1px solid rgba(239,68,68,0.25)',
                      borderRadius: 10,
                      fontSize: 12,
                      color: '#fca5a5',
                      lineHeight: 1.5,
                    }}>
                      ⚠️ <strong>Score capped:</strong> {penalty}
                    </div>
                  )}
                  {breakdown && (
                    <div className="an-score-bars">
                      {Object.entries(breakdown).map(([key, sig]) => {
                        const pct = sig.max > 0 ? (sig.score / sig.max) * 100 : 0;
                        const barColor = pct >= 70 ? 'green' : pct >= 40 ? 'yellow' : 'red';
                        return (
                          <div key={key} className="an-score-bar-row">
                            <span className="an-score-bar-label">{signalNames[key] || key}</span>
                            <div className="an-score-bar-track">
                              <div className={`an-score-bar-fill an-score-bar-fill-${barColor}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="an-score-bar-val">{sig.score}/{sig.max}</span>
                            <span className="an-score-bar-detail">{sig.detail}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Click Map ── */}
          <div className="an-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
              <p className="an-card-title" style={{ margin: 0 }}>Global Click Map</p>
              <div className="an-chart-tabs">
                <button
                  className={`an-chart-tab${clickMapTab === 'geo' ? ' active' : ''}`}
                  onClick={() => setClickMapTab('geo')}
                >🌍 Geographic</button>
                <button
                  className={`an-chart-tab${clickMapTab === 'date' ? ' active' : ''}`}
                  onClick={() => setClickMapTab('date')}
                  disabled={!isPro}
                  title={!isPro ? "Upgrade to PRO for Date Analytics" : ""}
                >📈 Date vs Clicks</button>
                <button
                  className={`an-chart-tab${clickMapTab === 'hourly' ? ' active' : ''}`}
                  onClick={() => setClickMapTab('hourly')}
                  disabled={!isPro}
                  title={!isPro ? "Upgrade to PRO for Hourly Analytics" : ""}
                >🕐 Hour of Day</button>
              </div>
            </div>
            <div>
              {clickMapTab === 'geo' && <GeographicChart analytics={analytics} geoRange={geoRange} setGeoRange={setGeoRange} />}
              {clickMapTab === 'date' && <DateClicksChart data={analytics?.clicksByDate} />}
              {clickMapTab === 'hourly' && <HourlyChart data={analytics?.hourlyData} hourlyRange={hourlyRange} setHourlyRange={setHourlyRange} />}
            </div>
          </div>
        </div>

        {/* ── Summary tables ── */}
        <div className="an-grid">
          <MiniTable
            title="Daily Clicks"
            rows={analytics.dailyClicks}
            cols={[
              { key: 'date', label: 'Date' },
              { key: 'click_count', label: 'Clicks', render: v => <span className="an-click-badge">{v}</span> }
            ]}
          />
          <MiniTable
            title="Device Types"
            rows={analytics.deviceData}
            cols={[
              { key: 'device_type', label: 'Device' },
              { key: 'count', label: 'Clicks', render: v => <span className="an-click-badge">{v}</span> }
            ]}
          />
          <MiniTable
            title="Top Countries"
            rows={analytics.geoData}
            cols={[
              { key: 'country', label: 'Country' },
              { key: 'count', label: 'Clicks', render: v => <span className="an-click-badge">{v}</span> }
            ]}
          />
          <MiniTable
            title="Top Browsers"
            rows={analytics.browserData}
            cols={[
              { key: 'browser', label: 'Browser' },
              { key: 'count', label: 'Clicks', render: v => <span className="an-click-badge">{v}</span> }
            ]}
          />
          <MiniTable
            title="Top Referrers"
            rows={analytics.referrerData}
            wrapperStyle={{ gridColumn: 'span 2' }}
            cols={[
              {
                key: 'referrer', label: 'Referrer',
                render: v => v ? (
                  <a className="an-ref-link" href={v} target="_blank" rel="noopener noreferrer">
                    {v.length > 60 ? v.substring(0, 60) + '…' : v}
                  </a>
                ) : <span style={{ color: '#334155' }}>Direct</span>
              },
              { key: 'count', label: 'Clicks', render: v => <span className="an-click-badge">{v}</span> }
            ]}
          />
        </div>

        {/* ── Individual Clicks ── */}
        <div className="an-card">
          <h3 className="an-clicks-title">👆 Individual Clicks</h3>
          {analytics.recentClicks.length === 0 ? (
            <p className="an-no-data">No clicks recorded yet.</p>
          ) : (() => {
            const totalClicks = analytics.recentClicks.length;
            const totalPages = Math.ceil(totalClicks / CLICKS_PER_PAGE);
            const pageClicks = analytics.recentClicks.slice(
              (clicksPage - 1) * CLICKS_PER_PAGE,
              clicksPage * CLICKS_PER_PAGE
            );
            return (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="an-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Referrer</th>
                        <th>Country</th>
                        <th>Device</th>
                        <th>Browser</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageClicks.map((click, i) => (
                        <tr key={i}>
                          <td style={{ whiteSpace: 'nowrap' }}>{new Date(click.clicked_at).toLocaleString()}</td>
                          <td>
                            {click.referrer ? (
                              <a className="an-ref-link" href={click.referrer} target="_blank" rel="noopener noreferrer">
                                {click.referrer.length > 28 ? click.referrer.substring(0, 28) + '…' : click.referrer}
                              </a>
                            ) : <span style={{ color: '#334155' }}>Direct</span>}
                          </td>
                          <td>{click.country || '—'}</td>
                          <td>{click.device_type || '—'}</td>
                          <td>{click.browser || '—'}</td>
                          <td>
                            <button className="an-view-btn" onClick={() => openModal(click)}>View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination controls */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
                    <button
                      className="an-state-btn"
                      style={{ padding: '6px 16px', fontSize: 13 }}
                      onClick={() => setClicksPage(p => Math.max(1, p - 1))}
                      disabled={clicksPage === 1}
                    >← Prev</button>
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      Page {clicksPage} of {totalPages} &nbsp;·&nbsp; {totalClicks} clicks
                    </span>
                    <button
                      className="an-state-btn"
                      style={{ padding: '6px 16px', fontSize: 13 }}
                      onClick={() => setClicksPage(p => Math.min(totalPages, p + 1))}
                      disabled={clicksPage === totalPages}
                    >Next →</button>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* ── Heal Info overlay ── */}
        {healInfoOpen && (
          <div className="an-modal-overlay" onClick={() => setHealInfoOpen(false)}>
            <div className="an-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
              <h2 className="an-modal-title">🏥 Self-Healing Links — How it works</h2>
              <p className="an-modal-sub">Automatically monitors your destination URLs and switches to a backup when something breaks — no manual action needed.</p>

              <div className="an-info-section">
                <h4>The problem it solves</h4>
                <p>When a destination URL goes down, visitors clicking your short link get an error page. Self-Healing detects this on its next scheduled check and instantly redirects to a backup URL, protecting your visitors and your brand reputation.</p>
              </div>

              <div className="an-info-section">
                <h4>Healing flow</h4>
                <table className="an-info-table">
                  <tbody>
                    <tr><td>1️⃣</td><td>Background worker runs a health check on your destination URL</td></tr>
                    <tr><td>2️⃣ Healthy</td><td>No action — redirect continues normally ✅</td></tr>
                    <tr><td>2️⃣ Unhealthy</td><td>Switch to the first available backup URL → alert sent 🔀</td></tr>
                    <tr><td>3️⃣ No backup</td><td>All targets failed → visitors see a friendly fallback page 🛡️</td></tr>
                    <tr><td>4️⃣ Recovery</td><td>Primary URL passes its next check → auto-switched back + alert sent 🟢</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="an-info-section">
                <h4>📧 When you receive email alerts</h4>
                <table className="an-info-table">
                  <thead><tr><th>Event</th><th>Alert</th></tr></thead>
                  <tbody>
                    <tr><td>🔀 Backup activated</td><td>Primary URL failed — visitors switched to your backup URL</td></tr>
                    <tr><td>🚨 All URLs failed</td><td>Primary + all backups unreachable (or no backup set) — fallback page shown</td></tr>
                    <tr><td>� Primary restored</td><td>Your primary URL is healthy again — redirects switched back</td></tr>
                  </tbody>
                </table>
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Alerts are sent to your configured <strong>Alert Email</strong>, or your account email if none is set.</p>
              </div>

              <div className="an-info-section">
                <h4>Health check frequency</h4>
                <table className="an-info-table">
                  <thead><tr><th>Link traffic (30 days)</th><th>Check interval</th></tr></thead>
                  <tbody>
                    <tr><td>Very High (1,000+ clicks)</td><td>Every 1 hour</td></tr>
                    <tr><td>High (100–999 clicks)</td><td>Every 6 hours</td></tr>
                    <tr><td>Medium (10–99 clicks)</td><td>Every 12 hours</td></tr>
                    <tr><td>Low (1–9 clicks)</td><td>Every 24 hours</td></tr>
                    <tr><td>Idle (0 clicks)</td><td>Every 24 hours</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="an-info-section">
                <h4>Plan limits</h4>
                <div className="an-info-legend">
                  <div className="an-info-legend-item">🟣 Pro — up to 5 enrolled links</div>
                  <div className="an-info-legend-item">💎 Business — up to 50 enrolled links</div>
                </div>
              </div>

              <button className="an-modal-close" onClick={() => setHealInfoOpen(false)}>Close</button>
            </div>
          </div>
        )}

        {/* ── Score Info overlay ── */}
        {scoreInfoOpen && (
          <div className="an-modal-overlay" onClick={() => setScoreInfoOpen(false)}>
            <div className="an-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
              <h2 className="an-modal-title">🔍 Link Intelligence Score</h2>
              <p className="an-modal-sub">A 0–100 composite score measuring link quality across 4 weighted categories.</p>

              <div className="an-info-section">
                <h4>How is it calculated?</h4>
                <table className="an-info-table">
                  <thead><tr><th>Category</th><th>Pts</th><th>What it measures</th></tr></thead>
                  <tbody>
                    <tr>
                      <td>📈 Engagement</td><td>30</td>
                      <td>Click trend (7-day vs 30-day pace), unique visitor ratio, and total click volume.</td>
                    </tr>
                    <tr>
                      <td>🛡️ Traffic Trust</td><td>30</td>
                      <td>Bot-like traffic ratio, IP diversity, and velocity anomaly detection (unusual click spikes).</td>
                    </tr>
                    <tr>
                      <td>🌐 Destination Health</td><td>20</td>
                      <td>HTTP status code of the destination URL, page response speed, and HTTPS/SSL.</td>
                    </tr>
                    <tr>
                      <td>✅ Compliance</td><td>20</td>
                      <td>Abuse report penalties, manual deactivation, and link lifespan (very short-lived links are penalised).</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="an-info-section">
                <h4>Score bands</h4>
                <div className="an-info-legend">
                  <div className="an-info-legend-item"><span>🟢</span> 80–100 — Healthy</div>
                  <div className="an-info-legend-item"><span>🟡</span> 50–79 — Fair</div>
                  <div className="an-info-legend-item"><span>🔴</span> 0–49 — Poor</div>
                </div>
              </div>

              <div className="an-info-section">
                <p>Scores are cached for <strong>24 hours</strong>. Click <strong>🔄 Refresh</strong> to force a fresh check.</p>
              </div>

              <button className="an-modal-close" onClick={() => setScoreInfoOpen(false)}>Close</button>
            </div>
          </div>
        )}

        {/* ── Click detail modal ── */}
        {modalIsOpen && selectedClick && (
          <div className="an-modal-overlay" onClick={closeModal}>
            <div className="an-modal" onClick={e => e.stopPropagation()}>
              <h2 className="an-modal-title">👆 Click Details</h2>
              <p className="an-modal-sub">Detailed information about this specific click</p>

              <div className="an-modal-grid">
                <div className="an-detail-card">
                  <h4>📅 Timing</h4>
                  <p className="an-detail-row"><strong>Time:</strong> {new Date(selectedClick.clicked_at).toLocaleString()}</p>
                  <p className="an-detail-row"><strong>Date:</strong> {new Date(selectedClick.clicked_at).toLocaleDateString()}</p>
                </div>
                <div className="an-detail-card">
                  <h4>🌐 Source</h4>
                  <p className="an-detail-row"><strong>Referrer:</strong></p>
                  <p className="an-detail-row">
                    {selectedClick.referrer ? (
                      <a className="an-ref-link" href={selectedClick.referrer} target="_blank" rel="noopener noreferrer">
                        {selectedClick.referrer.length > 40 ? selectedClick.referrer.substring(0, 40) + '…' : selectedClick.referrer}
                      </a>
                    ) : 'Direct'}
                  </p>
                </div>
                <div className="an-detail-card">
                  <h4>📍 Location</h4>
                  <p className="an-detail-row"><strong>Country:</strong> {selectedClick.country || '—'}</p>
                  <p className="an-detail-row"><strong>City:</strong> {selectedClick.city || 'Unknown'}</p>
                </div>
                <div className="an-detail-card">
                  <h4>📱 Device &amp; Browser</h4>
                  <p className="an-detail-row"><strong>Device:</strong> {selectedClick.device_type || '—'}</p>
                  <p className="an-detail-row"><strong>Browser:</strong> {selectedClick.browser || '—'}</p>
                  <p className="an-detail-row"><strong>OS:</strong> {selectedClick.os || '—'}</p>
                </div>
              </div>

              <div className="an-detail-card" style={{ marginBottom: 0 }}>
                <h4>🔧 Technical Details</h4>
                <p className="an-detail-row"><strong>IP (masked):</strong> {selectedClick.ip_address ? selectedClick.ip_address.replace(/\d+$/, '***') : 'Unknown'}</p>
                <p className="an-detail-row" style={{ marginTop: 8 }}><strong>User Agent:</strong></p>
                <div className="an-ua-box">{selectedClick.user_agent}</div>
              </div>

              <button className="an-modal-close" onClick={closeModal}>Close Details</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Analytics;
